/**
 * Asset library backed by IndexedDB.
 *
 * Each asset has a `scope`:
 *   - 'project': only visible inside its origin project (default for new uploads)
 *   - 'global':  visible in every project — promote to share across projects
 *
 * Asset key in IDB: `asset:${id}`. Listing scans the asset prefix.
 */

import { dbDelete, dbGet, dbKeys, dbSet } from '../db/idb';

const ASSET_PREFIX = 'asset:';

export type AssetKind = 'image' | 'video' | 'audio';
export type AssetScope = 'project' | 'global';

export interface Asset {
  id: string;
  kind: AssetKind;
  name: string;
  mime: string;
  /** data URL or http(s) URL */
  src: string;
  sizeBytes: number;
  createdAt: number;
  /** Project that owned the upload — preserved even after promotion. */
  projectId: string;
  scope: AssetScope;
}

const LISTENERS = new Set<() => void>();
function notify(): void {
  for (const fn of LISTENERS) fn();
}
export function subscribeAssets(fn: () => void): () => void {
  LISTENERS.add(fn);
  return () => LISTENERS.delete(fn);
}

function uid(): string {
  return `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function detectKind(mime: string): AssetKind {
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'image';
}

/** Read a File into a data URL + asset record. */
export async function uploadAsset(
  file: File,
  projectId: string,
  scope: AssetScope = 'project',
): Promise<Asset> {
  const src = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  const asset: Asset = {
    id: uid(),
    kind: detectKind(file.type),
    name: file.name,
    mime: file.type || 'application/octet-stream',
    src,
    sizeBytes: file.size,
    createdAt: Date.now(),
    projectId,
    scope,
  };
  await dbSet(ASSET_PREFIX + asset.id, asset);
  notify();
  return asset;
}

/** Save an external (URL or data URL) asset record without re-reading bytes. */
export async function recordAsset(
  partial: Omit<Asset, 'id' | 'createdAt'>,
): Promise<Asset> {
  const asset: Asset = { ...partial, id: uid(), createdAt: Date.now() };
  await dbSet(ASSET_PREFIX + asset.id, asset);
  notify();
  return asset;
}

export async function listAssets(): Promise<Asset[]> {
  const keys = await dbKeys(ASSET_PREFIX);
  const out: Asset[] = [];
  for (const k of keys) {
    const a = await dbGet<Asset>(k);
    if (a && typeof a === 'object' && 'id' in a) out.push(a);
  }
  out.sort((a, b) => b.createdAt - a.createdAt);
  return out;
}

/** Returns assets visible to the given project: its own + all globals. */
export async function listVisibleAssets(projectId: string): Promise<Asset[]> {
  const all = await listAssets();
  return all.filter((a) => a.scope === 'global' || a.projectId === projectId);
}

export async function getAsset(id: string): Promise<Asset | null> {
  return (await dbGet<Asset>(ASSET_PREFIX + id)) ?? null;
}

export async function deleteAsset(id: string): Promise<void> {
  await dbDelete(ASSET_PREFIX + id);
  notify();
}

export async function setAssetScope(id: string, scope: AssetScope): Promise<void> {
  const a = await getAsset(id);
  if (!a) return;
  a.scope = scope;
  await dbSet(ASSET_PREFIX + id, a);
  notify();
}

export async function renameAsset(id: string, name: string): Promise<void> {
  const a = await getAsset(id);
  if (!a) return;
  a.name = name;
  await dbSet(ASSET_PREFIX + id, a);
  notify();
}
